import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTranslate } from '../../../locales/hooks';
import { loginInterview } from '../../../auth/keycloak';

type Props = {
  onClose?: () => void;
  onLogin?: () => void | Promise<void>;
};

const InterviewAuthRequiredModal: React.FC<Props> = ({ onClose, onLogin }) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [hasLoginError, setHasLoginError] = useState(false);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    if (location.key === 'default') {
      navigate('/', { replace: true });
    } else {
      navigate(-1);
    }
  }, [onClose, navigate, location.key]);

  const handleLogin = useCallback(async () => {
    setHasLoginError(false);
    setIsLoggingIn(true);
    try {
      await (onLogin ? onLogin() : loginInterview());
    } catch (error) {
      console.error('Interview login failed', error);
      setHasLoginError(true);
      setIsLoggingIn(false);
    }
  }, [onLogin]);

  useEffect(() => {
    const onKeyDownCapture = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      handleClose();
    };
    window.addEventListener('keydown', onKeyDownCapture, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDownCapture, { capture: true });
  }, [handleClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-name="interview-auth-required-modal">
      <div
        className="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative bg-white rounded-[12px] border border-[#ddd] w-[520px] max-w-[92vw] shadow-xl">
        <div className="flex flex-col gap-[20px] p-[24px]">
          <div className="flex items-start justify-between gap-[10px]">
            <h2 className="font-['Manrope:Bold',sans-serif] font-bold leading-[1.4] text-[#0f172a] text-[20px]">
              {t('authRequiredModal__title')}
            </h2>
            <button
              onClick={handleClose}
              className="flex items-center justify-center p-[4px] rounded-[6px] hover:bg-[#f8fafc] transition-colors"
              aria-label={t('authRequiredModal__closeButton')}
            >
              <X className="w-[20px] h-[20px] text-[#334155]" />
            </button>
          </div>

          <div className="flex flex-col gap-[14px]">
            <p className="font-['Manrope:Regular',sans-serif] font-normal leading-[1.5] text-[#334155] text-[14px]">
              {t('authRequiredModal__intro')}
            </p>

            <p className="font-['Manrope:Regular',sans-serif] font-normal leading-[1.5] text-[#334155] text-[14px]">
              {t('authRequiredModal__rules')}
            </p>

            <div className="px-[12px] py-[10px] bg-[#fffbeb] border border-[#fde68a] rounded-[8px]">
              <p className="font-['Manrope:Regular',sans-serif] font-normal leading-[1.5] text-[#92400e] text-[13px]">
                {t('authRequiredModal__authWarning')}
              </p>
            </div>

            {hasLoginError && (
              <div className="px-[12px] py-[10px] bg-[#fef2f2] border border-[#fca5a5] rounded-[8px]" role="alert">
                <p className="font-['Manrope:Regular',sans-serif] font-normal leading-[1.5] text-[#991b1b] text-[13px]">
                  {t('authRequiredModal__loginError')}
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-[10px]">
            <button
              onClick={handleClose}
              disabled={isLoggingIn}
              className="px-[16px] py-[8px] rounded-[8px] border border-[#ddd] hover:bg-[#f8fafc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <p className="font-['Manrope:Medium',sans-serif] font-medium text-[#334155] text-[14px]">
                {t('authRequiredModal__closeButton')}
              </p>
            </button>
            <button
              onClick={handleLogin}
              disabled={isLoggingIn}
              className="px-[16px] py-[8px] bg-[#0f172a] rounded-[8px] hover:bg-[#334155] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <p className="font-['Manrope:Medium',sans-serif] font-medium text-white text-[14px]">
                {t('authRequiredModal__loginButton')}
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewAuthRequiredModal;
